import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { galleryCategories, galleryProjects } from "../data/site";
import { Seo, breadcrumbLd } from "../components/Seo";
import { PageHero, CTABand } from "../components/ui-blocks";

const Gallery = () => {
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null); // index within filtered
  const touchStartX = useRef(null);

  const filtered = category === "All" ? galleryProjects : galleryProjects.filter((p) => p.category === category);

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i > 0 ? i - 1 : filtered.length - 1)), [filtered.length]);
  const next = useCallback(() => setLightbox((i) => (i < filtered.length - 1 ? i + 1 : 0)), [filtered.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, prev, next]);

  const active = lightbox !== null ? filtered[lightbox] : null;

  return (
    <div>
      <Seo
        title="Gallery — Crafted Spaces"
        description="Browse a gallery of kitchen, island, bathroom, commercial, fireplace and custom stonework styles and materials by Premier Granite LLC."
        path="/gallery"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Gallery", path: "/gallery" }])}
      />
      <PageHero
        eyebrow="Gallery"
        title="Crafted Spaces"
        subtitle="See the craftsmanship, materials and finishes behind Premier Granite projects."
        image={galleryProjects[0].image}
        testid="gallery-hero"
      />

      <section className="section bg-white">
        <div className="container-custom">
          {/* Filters */}
          <div className="flex flex-wrap gap-2.5 justify-center mb-10" data-testid="gallery-filters">
            {galleryCategories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${
                  category === c
                    ? "bg-[#1A3D84] text-white border-[#1A3D84]"
                    : "bg-white text-neutral-700 border-neutral-300 hover:border-[#1A3D84]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Masonry grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]" data-testid="gallery-grid">
            {filtered.map((p, idx) => (
              <button
                key={p.id}
                onClick={() => setLightbox(idx)}
                className="img-zoom mb-4 block w-full rounded-lg overflow-hidden group relative break-inside-avoid"
                data-testid={`gallery-item-${p.id}`}
              >
                <img src={p.image} alt={`${p.title} — ${p.material}`} className="w-full h-auto object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div className="text-left">
                    <p className="text-white font-medium text-sm">{p.title}</p>
                    <p className="text-white/70 text-xs">{p.category} · {p.material}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <p className="text-center text-xs text-neutral-400 mt-10 max-w-2xl mx-auto">
            Images are representative of the styles, materials and finishes we work with.
          </p>
        </div>
      </section>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          data-testid="gallery-lightbox"
          onClick={close}
          onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            if (dx > 50) prev();
            else if (dx < -50) next();
            touchStartX.current = null;
          }}
        >
          <button onClick={close} className="absolute top-5 right-5 text-white/80 hover:text-white" aria-label="Close" data-testid="lightbox-close">
            <X size={30} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 md:left-8 text-white/80 hover:text-white" aria-label="Previous">
            <ChevronLeft size={38} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 md:right-8 text-white/80 hover:text-white" aria-label="Next">
            <ChevronRight size={38} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={active.image} alt={`${active.title} — ${active.material}`} className="w-full max-h-[80vh] object-contain rounded" />
            <div className="text-center mt-4">
              <p className="text-white font-medium">{active.title}</p>
              <p className="text-white/60 text-sm">{active.category} · {active.material}</p>
            </div>
          </div>
        </div>
      )}

      <CTABand />
    </div>
  );
};

export default Gallery;
